import { useCallback, useEffect, useState } from 'react'
import { useAuth } from '@/hooks/use-auth'
import { useCreateEInvoiceDraft, useEInvoice } from '@/hooks/use-einvoice-query'
import {
    usePublishEInvoice,
    useRefreshEInvoice,
    useRefreshEInvoiceDraft,
} from '@/hooks/use-einvoice-draft-mutations'
import { useFeatureFlag } from '@/hooks/use-feature-flag'
import { useWorkflowTracking } from '@/hooks/use-workflow-tracking'
import { EInvoicePanelState } from '@/components/sections/einvoice-panel-state'
import { EInvoiceQueryState } from '@/components/sections/einvoice-query-state'
import {
    QuickSaleAutoPublishNotice,
    useQuickSaleAutoPublish,
} from '@/components/pos/pos-einvoice-auto-publish'

// Saved draft preview and explicit publication inside the POS success popup.
// orderStatus: đơn "Bán ngay" chốt xong là phát hành được ngay, không chờ thủ
// kho xuất (M-132); lên delivered (thủ kho đã xuất) thì tải lại HĐĐT để thấy
// nháp do hệ thống tạo sau khi giao. autoPublish (M-167): đơn vừa "Bán ngay"
// có tích hóa đơn thì tự phát hành, không bày chuỗi xem nháp → xác nhận.
export function PosEInvoiceStatus({
    salesOrderID,
    orderStatus,
    isQuickSale,
    autoPublish = false,
}: {
    salesOrderID: string
    orderStatus?: string
    isQuickSale?: boolean
    autoPublish?: boolean
}) {
    const enabled = useFeatureFlag('dev-einvoice')
    const query = useEInvoice(salesOrderID, Boolean(enabled))
    const { refetch } = query
    const delivered = orderStatus === 'delivered'
    useEffect(() => {
        if (enabled && delivered) void refetch()
    }, [enabled, delivered, refetch])
    const draft = useCreateEInvoiceDraft()
    const publish = usePublishEInvoice()
    const refreshDraft = useRefreshEInvoiceDraft()
    const refresh = useRefreshEInvoice()
    const { hasPermission } = useAuth()
    const canExport = hasPermission('invoices.export')
    const [created, setCreated] = useState(false)
    // The preview opens once from "Phát hành"; later viewers start closed.
    const clearCreated = useCallback(() => setCreated(false), [])
    const draftWorkflow = useWorkflowTracking('pos_einvoice_draft', {
        autoStart: false,
        attemptKey: salesOrderID,
        operation: 'create',
    })
    const publishWorkflow = useWorkflowTracking('pos_einvoice_push', {
        autoStart: false,
        attemptKey: salesOrderID,
        operation: 'submit',
    })
    const auto = useQuickSaleAutoPublish({
        orderId: salesOrderID,
        requested: Boolean(enabled && autoPublish && canExport),
        workflow: publishWorkflow,
    })
    if (!enabled) return null
    if (auto.pending)
        return (
            <QuickSaleAutoPublishNotice
                pending
                succeeded={false}
                error={null}
                noPermission={false}
            />
        )
    const notice = (
        <QuickSaleAutoPublishNotice
            pending={false}
            succeeded={auto.succeeded}
            error={auto.error}
            noPermission={autoPublish && !canExport}
            invoice={query.data}
        />
    )
    if (query.isLoading || query.isError)
        return (
            <div className="space-y-2">
                {notice}
                <EInvoiceQueryState
                    error={query.error}
                    retry={() => void query.refetch()}
                />
            </div>
        )
    return (
        <div className="space-y-2">
            {notice}
            <EInvoicePanelState
                key={salesOrderID}
                orderId={salesOrderID}
                orderStatus={orderStatus}
                isQuickSale={isQuickSale}
                invoice={query.data ?? null}
                canMutate={
                    canExport &&
                    !['cancelled', 'returned'].includes(orderStatus ?? '')
                }
                pushing={draft.isPending}
                refreshing={refresh.isPending}
                initiallyOpen={created}
                onInitiallyOpened={clearCreated}
                onPush={() => {
                    void draftWorkflow
                        .run(
                            () => draft.mutateAsync(salesOrderID),
                            (data) => data?.id,
                        )
                        .then(() => setCreated(true))
                        .catch(() => {})
                }}
                onRefresh={() => {
                    void refresh.mutateAsync(salesOrderID).catch(() => {})
                }}
                onPublish={(previewToken) => {
                    publishWorkflow.setContext({ publishTrigger: 'manual' })
                    if (
                        orderStatus === 'confirmed' ||
                        orderStatus === 'delivered'
                    )
                        publishWorkflow.setContext({
                            orderStage:
                                orderStatus === 'delivered'
                                    ? 'delivered'
                                    : 'awaiting_stock_out',
                        })
                    return publishWorkflow.run(
                        () =>
                            publish.mutateAsync({
                                orderId: salesOrderID,
                                previewToken,
                            }),
                        (data) => data?.id,
                    )
                }}
                onRefreshDraft={(buyer, content, reset) =>
                    refreshDraft.mutateAsync({
                        orderId: salesOrderID,
                        buyer,
                        draft: content,
                        reset,
                    })
                }
            />
        </div>
    )
}
